import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, unwrap } from '@/lib/api'
import type { DataEnvelope, InvitationPreview, LoginResponse, Me, Tenant, User } from '@/types/api'

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
  invitation: (token: string) => [...authKeys.all, 'invitation', token] as const,
}

/** `/auth/me` — resolves to `null` when signed out (401) instead of throwing. */
export const meQueryOptions = queryOptions({
  queryKey: authKeys.me(),
  queryFn: async ({ signal }): Promise<Me | null> => {
    try {
      return await unwrap(api.get<DataEnvelope<Me>>('/auth/me', { signal, silent: true }))
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null
      throw error
    }
  },
  staleTime: 60_000,
})

export function useMe() {
  return useQuery(meQueryOptions)
}

/** The signed-in identity. Only use below an authenticated route guard. */
export function useAuthedMe(): Me {
  const { data } = useMe()
  if (!data) throw new Error('useAuthedMe() used outside an authenticated route')
  return data
}

export interface LoginInput {
  email: string
  password: string
  remember?: boolean
}

export function useLogin() {
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<LoginResponse>('/auth/login', input, { silent: true }),
  })
}

export function useTwoFactorChallenge() {
  return useMutation({
    mutationFn: (input: { code: string } | { recovery_code: string }) =>
      unwrap(api.post<DataEnvelope<User>>('/auth/two-factor-challenge', input, { silent: true })),
  })
}

export interface RegisterInput {
  name: string
  email: string
  password: string
  password_confirmation: string
}

export function useRegister() {
  return useMutation({
    mutationFn: (input: RegisterInput) => unwrap(api.post<DataEnvelope<User>>('/auth/register', input, { silent: true })),
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout', undefined, { silent: true }),
    onSettled: () => {
      qc.setQueryData(authKeys.me(), null)
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' && q.queryKey[0] !== 'plans' })
    },
  })
}

export function useCreateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; timezone?: string }) => unwrap(api.post<DataEnvelope<Tenant>>('/tenants', input)),
    onSuccess: async () => {
      // A new tenant becomes current: everything tenant-scoped is stale.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' && q.queryKey[0] !== 'plans' })
      await qc.invalidateQueries({ queryKey: authKeys.me() })
    },
  })
}

export function useSwitchTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tenantId: string) => unwrap(api.post<DataEnvelope<Me>>('/tenants/switch', { tenant_id: tenantId })),
    onSuccess: (me) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' && q.queryKey[0] !== 'plans' })
      qc.setQueryData(authKeys.me(), me)
    },
  })
}

export function invitationPreviewOptions(token: string) {
  return queryOptions({
    queryKey: authKeys.invitation(token),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<InvitationPreview>>(`/invitations/token/${encodeURIComponent(token)}`, { signal, silent: true })),
    retry: false,
  })
}

export function useAcceptInvitation(token: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<unknown>(`/invitations/token/${encodeURIComponent(token)}/accept`),
    onSuccess: async () => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' && q.queryKey[0] !== 'plans' })
      await qc.invalidateQueries({ queryKey: authKeys.me() })
    },
  })
}

export function useStopImpersonation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/admin/impersonation/stop'),
    onSuccess: async () => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' && q.queryKey[0] !== 'plans' })
      await qc.invalidateQueries({ queryKey: authKeys.me() })
    },
  })
}
